/** Add / edit / delete a scheduled shift — a centered <Modal> popup matching
 *  the rest of the app (members/products/staff). Employee + date + start/end
 *  time, optional location (shown only for multi-location tenants). */
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/ui/Button.js';
import { Field, Input, Select } from '../../../components/ui/Field.js';
import { Modal } from '../../../components/ui/Modal.js';
import { toast } from '../../../components/ui/Toaster.js';
import type { LocationRow } from '../../../lib/locations.js';
import { type ShiftRow, type StaffRow } from '../hooks/useStaff.js';

import { combineDateTime, toDateInput, toTimeInput } from './scheduleUtils.js';
import { useCreateShift, useDeleteShift, useUpdateShift } from './useScheduleMutations.js';

/** What the modal opens with: an existing shift (edit) or a staff+day (add). */
export interface ShiftDraft {
  shift?: ShiftRow | null;
  staffId?: string;
  date?: Date;
}

export function ShiftModal({
  open,
  onOpenChange,
  staff,
  locations,
  draft,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  staff: StaffRow[];
  locations: LocationRow[];
  draft: ShiftDraft | null;
}): ReactNode {
  const { t } = useTranslation();
  const create = useCreateShift();
  const update = useUpdateShift();
  const remove = useDeleteShift();
  const editing = draft?.shift ?? null;
  const isEdit = editing != null;
  const saving = create.isPending || update.isPending;
  const pending = saving || remove.isPending;

  const [employeeId, setEmployeeId] = useState('');
  const [dateStr, setDateStr] = useState('');
  const [startStr, setStartStr] = useState('09:00');
  const [endStr, setEndStr] = useState('17:00');
  const [locationId, setLocationId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editing) {
      setEmployeeId(editing.staff_id);
      const start = editing.scheduled_start ? new Date(editing.scheduled_start) : new Date();
      setDateStr(toDateInput(start));
      setStartStr(editing.scheduled_start ? toTimeInput(editing.scheduled_start) : '09:00');
      setEndStr(editing.scheduled_end ? toTimeInput(editing.scheduled_end) : '17:00');
      setLocationId(editing.location_id ?? '');
    } else {
      setEmployeeId(draft?.staffId ?? staff[0]?.id ?? '');
      setDateStr(toDateInput(draft?.date ?? new Date()));
      setStartStr('09:00');
      setEndStr('17:00');
      setLocationId('');
    }
  }, [open, draft, editing, staff]);

  async function submit(): Promise<void> {
    setError(null);
    if (!employeeId) {
      setError(t('staff.schedule.employee'));
      return;
    }
    const start = combineDateTime(dateStr, startStr);
    const end = combineDateTime(dateStr, endStr);
    if (end.getTime() <= start.getTime()) {
      setError(t('staff.schedule.end_after_start'));
      return;
    }
    const input = {
      staffId: employeeId,
      locationId: locationId || null,
      scheduledStart: start.toISOString(),
      scheduledEnd: end.toISOString(),
    };
    try {
      if (isEdit && editing) {
        await update.mutateAsync({ id: editing.id, input });
      } else {
        await create.mutateAsync(input);
      }
      toast.success(t('staff.schedule.shift_saved'));
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onDelete(): Promise<void> {
    if (!editing) return;
    try {
      await remove.mutateAsync(editing.id);
      toast.success(t('staff.schedule.shift_deleted'));
      onOpenChange(false);
    } catch (e) {
      toast.error(t('common.error'), e instanceof Error ? e.message : undefined);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!pending) onOpenChange(o);
      }}
      title={t(isEdit ? 'staff.schedule.edit_shift' : 'staff.schedule.add_shift')}
      footer={
        <>
          {isEdit ? (
            <Button
              variant="destructive"
              className="mr-auto"
              loading={remove.isPending}
              disabled={saving}
              onClick={() => void onDelete()}
            >
              {t('staff.schedule.delete_shift')}
            </Button>
          ) : null}
          <Button variant="secondary" disabled={pending} onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={remove.isPending}
            onClick={() => void submit()}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Field label={t('staff.schedule.employee')} required>
          {(id) => (
            <Select
              id={id}
              aria-label="employee"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            >
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.first_name} {s.last_name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label={t('staff.schedule.date')} required>
          {(id) => (
            <Input
              id={id}
              type="date"
              aria-label="date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
            />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('staff.schedule.start')} required>
            {(id) => (
              <Input
                id={id}
                type="time"
                aria-label="start"
                value={startStr}
                onChange={(e) => setStartStr(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('staff.schedule.end')} required>
            {(id) => (
              <Input
                id={id}
                type="time"
                aria-label="end"
                value={endStr}
                onChange={(e) => setEndStr(e.target.value)}
              />
            )}
          </Field>
        </div>

        {locations.length > 1 ? (
          <Field label={t('staff.schedule.location')}>
            {(id) => (
              <Select
                id={id}
                aria-label="location"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
              >
                <option value="">{t('staff.schedule.no_location')}</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        {error ? (
          <p role="alert" className="text-sm font-medium text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
